import Header from "../components/Header";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from "react-router-dom";
import {
    Avatar, Box, Breadcrumbs, Button, Card, Chip, Divider, InputAdornment, Link, Skeleton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { FormatDate } from "../../utils/FormatDate";
import { MascaraTelefone } from "../../utils/MascaraTelefone";

const corPrimaria = "#0b243d";

// O back seleciona "Nome", "Email" e "Telefone" (o model usa minúsculas); aceita as duas formas
const dadoUsuario = (descontoUsado, campo) =>
    descontoUsado?.usuario?.[campo] ?? descontoUsado?.usuario?.[campo.charAt(0).toUpperCase() + campo.slice(1)] ?? "";

const normalizar = (texto) => String(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const iniciais = (nome) => String(nome || "?").trim().split(/\s+/).slice(0, 2).map((parte) => parte[0]?.toUpperCase()).join("");
const textoMeses = (meses) => (Number(meses) === 1 ? "1 mês" : `${meses} meses`);

function Informacao({ titulo, children }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{titulo}</Typography>
            <Box sx={{ fontWeight: 600, fontSize: 18 }}>{children}</Box>
        </Box>
    );
}

function DescontosUsados() {
    const { idDesconto } = useParams();
    const navigate = useNavigate();

    const token = localStorage.getItem('token');
    const [descontosUsados, setDescontosUsados] = useState([]);
    const [desconto, setDesconto] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [busca, setBusca] = useState("");

    useEffect(() => {
        const getDescontosUsados = async () => {
            try {
                // Dados do código vêm à parte para aparecerem mesmo quando ninguém usou o desconto
                const [responseUsados, responseDesconto] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/getDescontosUsados/${idDesconto}`, {
                        headers: { Authorization: token }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/getDescontoById/${idDesconto}`, {
                        headers: { Authorization: token }
                    }).catch(() => null), // opcional: sem ele a lista de usos ainda aparece
                ]);
                setDescontosUsados(responseUsados.data.descontosUsados || []);
                setDesconto(responseDesconto?.data?.desconto || null);
            } catch (err) {
                console.error(err);
            } finally {
                setCarregando(false);
            }
        }
        getDescontosUsados();
    }, [idDesconto, token])

    const usosFiltrados = useMemo(() => {
        const termo = normalizar(busca.trim());
        return descontosUsados
            .filter((uso) => !termo || ["nome", "email", "telefone"].some((campo) => normalizar(dadoUsuario(uso, campo)).includes(termo)))
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    }, [descontosUsados, busca]);

    // Se a busca do desconto falhar, usa o que veio junto com os usos
    const codigo = desconto?.discountCode || descontosUsados[0]?.desconto?.discountCode;
    const valor = desconto?.valorDesconto ?? descontosUsados[0]?.desconto?.valorDesconto;

    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" href="/Dashboard">
                            Dashboard
                        </Link>
                        <Link underline="hover" color="inherit" href="/Descontos">
                            Descontos
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Descontos Usados</Typography>
                    </Breadcrumbs>

                    <Box sx={{ mt: 2, mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Clientes que usaram o desconto</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Quem aplicou este código ao assinar um plano.
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            sx={{ textTransform: "none", color: corPrimaria }}
                            onClick={() => navigate("/Descontos")}
                        >
                            Voltar para descontos
                        </Button>
                    </Box>

                    {/* Resumo do código */}
                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                        <Box sx={{ p: 2.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 2, md: 4 } }}>
                            {carregando ? (
                                <Skeleton width="60%" height={48} />
                            ) : (
                                <>
                                    <Informacao titulo="Código">
                                        <Box component="span" sx={{ fontFamily: "monospace", px: 1, py: 0.25, borderRadius: 1, backgroundColor: "#f0f3f7", color: corPrimaria }}>
                                            {codigo || `#${idDesconto}`}
                                        </Box>
                                    </Informacao>
                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                                    <Informacao titulo="Desconto">
                                        <Box component="span" sx={{ color: "success.main" }}>{valor !== undefined ? `${valor}%` : "—"}</Box>
                                    </Informacao>
                                    {desconto?.duracaoMeses !== undefined && (
                                        <>
                                            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                                            <Informacao titulo="Duração">{textoMeses(desconto.duracaoMeses)}</Informacao>
                                        </>
                                    )}
                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                                    <Informacao titulo="Usos">{descontosUsados.length}</Informacao>
                                    {desconto && (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={desconto.status ? "success" : "default"}
                                            label={desconto.status ? "Ativo" : "Inativo"}
                                            sx={{ ml: { md: "auto" } }}
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <Box sx={{ p: 2.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar por nome, e-mail ou telefone"
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Box>

                        <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ minWidth: 680 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#fafafa", "& th": { fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 } }}>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>Telefone</TableCell>
                                        <TableCell>Usado em</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carregando ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 4 }).map((__, j) => (
                                                    <TableCell key={j} sx={{ py: 2 }}><Skeleton /></TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : usosFiltrados.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ py: 6, textAlign: "center", borderBottom: 0 }}>
                                                <PeopleOutlineIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                                                <Typography sx={{ fontWeight: 500, mt: 1 }}>
                                                    {busca ? "Nenhum cliente encontrado" : "Nenhum cliente usou esse código de desconto"}
                                                </Typography>
                                                {busca && <Typography variant="body2" color="text.secondary">Tente outro termo de busca.</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        usosFiltrados.map((descontoUsado) => {
                                            const nome = dadoUsuario(descontoUsado, "nome");
                                            const telefone = dadoUsuario(descontoUsado, "telefone");
                                            const idUsuario = descontoUsado?.usuario?.idUsuario ?? descontoUsado?.idUsuario;
                                            return (
                                                <TableRow key={descontoUsado?.idDescontoUsado} hover sx={{ "& td": { py: 1.5 } }}>
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar sx={{ width: 36, height: 36, fontSize: 14, backgroundColor: corPrimaria }}>
                                                                {iniciais(nome)}
                                                            </Avatar>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{nome || "Cliente sem nome"}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{dadoUsuario(descontoUsado, "email")}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{telefone ? MascaraTelefone(telefone) : "—"}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{descontoUsado?.updatedAt ? FormatDate(descontoUsado.updatedAt) : "—"}</TableCell>
                                                    <TableCell align="right">
                                                        {idUsuario && (
                                                            <Button
                                                                size="small"
                                                                endIcon={<ArrowForwardIcon fontSize="small" />}
                                                                sx={{ textTransform: "none", color: corPrimaria, whiteSpace: "nowrap" }}
                                                                onClick={() => navigate(`/EmitirNota/Perfil/${idUsuario}`)}
                                                            >
                                                                Abrir perfil
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Box>
            </Box>
        </div>
    );
}

export default DescontosUsados;
